import styled from "styled-components";

import theme from "@ui/themes/default";
import { rem, transition } from "@ui/helpers";
import { changeBgColor } from "@ui/animations";

export interface RangePickerState {
  startDate: Date | undefined;
  endDate: Date | undefined;
  enteredTo: Date | undefined;
}

export enum THEME_VARIANT {
  CLIENT = "Client",
  MAID = "Maid",
}

const dayContainerSize = {
  xsmall: rem(30),
  small: rem(36),
  medium: rem(42),
  large: rem(52),
};

const addRangeAnimationDelay = (selector: string) => {
  let styles = "";

  for (let i = 0; i <= 7; i++) {
    styles += `
        ${selector}:nth-child(${i}):before {
          animation-delay: 0.0${i}s;
         }
       `;
  }

  return `${styles}`;
};

export const getStyling = (variant: THEME_VARIANT): string => {
  const day = ".DayPicker-Day";
  const week = ".DayPicker-Week";

  return `
      .DayPicker {
        --calendarDayActive: ${
          variant === THEME_VARIANT.MAID
            ? theme.palette.red300
            : theme.palette.blue500
        };
        --calendarDayBorder: ${
          variant === THEME_VARIANT.MAID
            ? theme.palette.red50
            : theme.palette.blue50
        };
        --calendarDayLight: ${
          variant === THEME_VARIANT.MAID
            ? theme.palette.red50
            : theme.palette.blue50
        };
        
        font-size: ${theme.fonts.sizes.caption};
        width: 100%;
      }

      .DayPicker-wrapper {
        width: 100%;
        margin-top: ${theme.margins.base_x6};
      }

      ${theme.breakpoints.desktop} {
        .DayPicker-wrapper {
          padding-bottom: ${theme.margins.base_x5};
          min-height: ${rem(385)};
          margin-top: 0;
        }
      }

      .DayPicker-wrapper:focus {
        outline: 0;
      }

      .DayPicker-Caption {
        color: var(--calendarDayActive);
        text-align: left;
        font-size: ${theme.fonts.sizes.body};
        font-weight: ${theme.fonts.weights.bold};
        margin-bottom: ${theme.margins.base_x2};
      }

      ${theme.breakpoints.desktop} {
        .DayPicker-Caption {
          text-align: center;
          font-size: ${theme.fonts.sizes.body};
          margin-top: ${rem(10)};
        }
      }

      .DayPicker-NavBarContainer {
        position: absolute;
        padding: 0 ${theme.margins.base_x2};
        top: ${theme.margins.base_x3};
        width: 100%;
        height: ${theme.margins.base_x9};
      }

      .DayPicker-NavBar {
        display: flex;
        justify-content: space-between;
      }

      .DayPicker-NavButtonCustom {
        color: var(--calendarDayActive);
        width: ${rem(20)};
        margin-top: -${theme.margins.half};
        cursor: pointer;
      }

      .DayPicker-NavButtonCustom:hover svg g g {
        fill: ${theme.palette.grey700};
      }

      .DayPicker-NavButtonCustom--prev svg {
        transform: rotate(90deg);
      }

      .DayPicker-NavButtonCustom--next svg {
        transform: rotate(-90deg);
        margin-left: -${theme.margins.base};
      }

      .DayPicker-Month {
        margin-bottom: ${theme.margins.base_x2};
      }

      ${theme.breakpoints.desktop} {
        .DayPicker-Month {
          margin-bottom: 0;
        }
      }

      .DayPicker-Months {
        align-items: start;
      }

      .DayPicker-Months .DayPicker-Weekdays {
        color: ${theme.palette.grey800};
        display: none;
      }

      ${theme.breakpoints.desktop} {
        .DayPicker-Months .DayPicker-Weekdays {
          display: table-header-group;
        }
      }

      .DayPicker-Weekdays {
        display: none;
      }

      ${theme.breakpoints.desktop} {
        .DayPicker-Weekdays {
          display: table-header-group;
        }
      }

      .DayPicker-WeekdaysRow {
        display: flex;
        justify-content: space-evenly;
      }

      .DayPicker-WeekdaysRow div:last-of-type {
        font-weight: ${theme.fonts.weights.bold};
      }

      ${theme.orientation.landscape} {
        .DayPicker-WeekdaysRow {
          max-width: ${theme.container.calendar};
          margin: 0 auto;
          justify-content: space-around;
        }
      }

      ${theme.breakpoints.tablet} {
        .DayPicker-WeekdaysRow {
          max-width: ${theme.container.calendar};
          margin: 0 auto;
          justify-content: space-around;
        }
      }

      ${theme.breakpoints.desktop} {
        .DayPicker-WeekdaysRow {
          display: table-row;
        }
      }

      .DayPicker-Weekday {
        padding-bottom: ${rem(10)};
        color: ${theme.palette.grey700};
      }

      ${theme.breakpoints.desktop} {
        .DayPicker-Weekday {
          padding-bottom: ${rem(5)};
        }
      }

      ${theme.breakpoints.desktop} {
        .DayPicker-Body {
          transform: translateY(${theme.margins.base_x2});
        }
      }

      /* Last day in every row */
      ${week} div:last-of-type${day}--selected:not(${day}--disabled):not(${day}--outside):before {
        border-top-right-radius: 50%;
        border-bottom-right-radius: 50%;
        width: ${dayContainerSize.medium};
      }

      ${theme.breakpoints.desktop} {
        ${week} div:last-of-type${day}--selected:not(${day}--disabled):not(${day}--outside):before {
          width: ${dayContainerSize.xsmall}
        }
      }

      /* First day in every row */
      ${week} div:first-of-type${day}--selected:not(${day}--disabled):not(${day}--outside):before {
        border-top-left-radius: 45%;
        border-bottom-left-radius: 45%;
      }
      
      /*** Global day selector ***/
      ${day} {
        position: relative;
        outline: none;
        width: ${dayContainerSize.large};
        height: ${dayContainerSize.large};
      }

      ${theme.breakpoints.desktop} {
        ${day} {
          width: ${dayContainerSize.small};
          height: ${dayContainerSize.medium};
        }
      }

      ${day}:after {
        content: '';
        position: absolute;
        width: ${dayContainerSize.medium};
        height: ${dayContainerSize.medium};
        border: ${rem(1)} solid var(--calendarDayBorder);
        border-radius: 100%;
        pointer-events: none;
        top: 50%;
        left: 50%;
        transform: translate(-50%, -50%);
        z-index: -1;
        transition: ${transition("background-color")};
      }

      ${day}:before {
        content: '';
        transition: ${transition("background-color")};
      }

      ${theme.breakpoints.desktop} {
        ${day}:after {
          width: ${dayContainerSize.xsmall};
          height: ${dayContainerSize.xsmall};
        }
      }

      ${day}:not(${day}--outside):hover {
        background-color: transparent;
      }

      ${day}:not(${day}--outside):hover:after {
        content: '';
        background-color: var(--calendarDayLight);
        border-color: var(--calendarDayActive);
        transition: ${transition([
          "background-color",
          "border-color",
          "color",
        ])};
      }
      /*** end Global day selector ***/

      /* Inactive day */
      ${day}--outside {
        opacity: 0;
        pointer-events: none;
      }

      /*** First selected day in range ***/
      ${day}--selected${day}--start${day}--hasRangeStart:not(${day}--disabled):not(${day}--outside):not(${day}--end):not(${day}--lastOfMonth):not(${day}--weekEnd):not(${day}--enteredTo):before {
        content: '';
        position: absolute;
        background-color: var(--calendarDayLight);
        border-radius: 50%;
        border-top-right-radius: 0;
        border-bottom-right-radius: 0;
        width: 100%;
        height: ${dayContainerSize.medium};
        left: ${rem(3)};
        top: ${rem(5)};
        z-index: -1;
      }

      ${theme.breakpoints.desktop} {
        ${day}--selected${day}--start${day}--hasRangeStart:not(${day}--disabled):not(${day}--outside):not(${day}--end):not(${day}--lastOfMonth):not(${day}--weekEnd):not(${day}--enteredTo):before {
          height: ${dayContainerSize.xsmall};
          top: calc(2 * ${rem(3)});
        }
      }

      ${day}--selected${day}--start:not(${day}--disabled):not(${day}--outside):not(${day}--end) {
        color: ${theme.palette.white}
      }

      ${day}--selected${day}--start:not(${day}--disabled):not(${day}--outside):not(${day}--end):after {
        background-color: var(--calendarDayActive);
        border-color: var(--calendarDayActive);
      }
      /*** end First selected day in range ***/

      /* First day of month */
      ${day}--selected${day}--firstOfMonth:not(${day}--disabled):not(${day}--outside):not(${day}--end):not(${day}--start):before {
        border-top-left-radius: 45%;
        border-bottom-left-radius: 45%;
      }

      /* Last day of month */
      ${day}--selected${day}--lastOfMonth:not(${day}--disabled):not(${day}--outside):not(${day}--end):not(${day}--start):before {
        border-top-right-radius: 50%;
        border-bottom-right-radius: 50%;
        width: ${dayContainerSize.medium};
      }

      ${theme.breakpoints.desktop} {
        ${day}--selected${day}--lastOfMonth:not(${day}--disabled):not(${day}--outside):not(${day}--end):not(${day}--start):before {
          width: ${dayContainerSize.xsmall}
        }
      }

      /*** Every selected day ***/
      ${day}--selected:not(${day}--disabled):not(${day}--outside):after {
        border: ${rem(1)} solid var(--calendarDayBorder);
        transition: ${transition("background-color")};
      }
      
      ${day}--selected:not(${day}--disabled):not(${day}--outside) {
        background-color: transparent;
        color: ${theme.palette.grey800};
      }

      ${day}--selected:not(${day}--disabled):not(${day}--outside):hover {
        background-color: transparent;
      }

      ${day}--selected:not(${day}--disabled):not(${day}--outside):not(${day}--start):not(${day}--end):before {
        content: '';
        position: absolute;
        border-radius: 50%;
        border-radius: 0;
        width: 100%;
        height: ${dayContainerSize.medium};
        left: ${rem(3)};
        top: ${rem(5)};
        z-index: -1;
        animation: changeBgColor 0.05s;
        animation-fill-mode: forwards;
      }

      ${addRangeAnimationDelay(
        `${day}--selected:not(${day}--disabled):not(${day}--outside):not(${day}--start):not(${day}--end)`,
      )}

      ${theme.breakpoints.desktop} {
        ${day}--selected:not(${day}--disabled):not(${day}--outside):not(${day}--start):not(${day}--end):before {
          content: '';
          height: ${dayContainerSize.xsmall};
          top: calc(2 * ${rem(3)});
        }
      }
      /*** end Every selected day ***/

      /*** Last selected day in range ***/
      ${day}--selected${day}--end:not(${day}--disabled):not(${day}--outside):not(${day}--start) {
        color: ${theme.palette.white};
        transition: ${transition("color")};
      }

      ${day}--selected${day}--end:not(${day}--disabled):not(${day}--outside):not(${day}--start):after {
        background-color: var(--calendarDayActive);
        border-color: var(--calendarDayActive);
      }

      ${day}--selected${day}--end:not(${day}--disabled):not(${day}--outside):not(${day}--start):not(${day}--firstOfMonth):not(${day}--weekStart):before {
        content: '';
        position: absolute;
        background-color: var(--calendarDayLight);
        border-radius: 50%;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        width: ${dayContainerSize.xsmall};
        height: ${dayContainerSize.medium};
        left: ${rem(3)};
        top: ${rem(5)};
        z-index: -1;
      }

      ${theme.breakpoints.desktop} {
        ${day}--selected${day}--end:not(${day}--disabled):not(${day}--outside):not(${day}--start):not(${day}--firstOfMonth):not(${day}--weekStart):before {
          content: '';
          height: ${dayContainerSize.xsmall};
          top: calc(2 * ${rem(3)});
        }
      }
      /*** end Last selected day in range ***/

      /* Hover over not selected day */
      .DayPicker:not(.DayPicker--interactionDisabled) ${day}:not(${day}--disabled):not(${day}--selected):not(${day}--outside):hover {
        background-color: transparent;
      }

      /*** Day selected as both start and end of range ***/
      ${day}--selected${day}--start${day}--end {
        color: ${theme.palette.white};
      }

      ${day}--selected${day}--start${day}--end:after {
        background-color: var(--calendarDayActive);
        border-color: var(--calendarDayActive);
      }
      /*** end Day selected as both start and end of range ***/

      /* Currently hovered day */
      ${day}--selected${day}--enteredTo:not(${day}--disabled):not(${day}--outside):not(${day}--start):before {
        border-radius: 50%;
        border-top-left-radius: 0;
        border-bottom-left-radius: 0;
        width: ${dayContainerSize.xsmall};
      }

      /* Day outside of given month */
      ${day}--outside {
        pointer-events: none;
      }

      /* Past Day */
      ${day}--past:not(${day}--outside) {
        pointer-events: none;
        color: ${theme.palette.grey300};
      }

      ${day}--past:not(${day}--outside):after {
        border-color: ${theme.palette.grey100};
      }

      /* Current day */
      ${day}--today {
        color: var(--calendarDayActive)
      }

      ${changeBgColor(
        "inherit",
        "var(--calendarDayLight)",
        "var(--calendarDayLight)",
      )}
    `;
};

export const DayPickerMobileMonths = styled.div`
  position: absolute;
  display: flex;
  justify-content: space-around;
  width: calc(100% - ${props => props.theme.margins.base_x4});
  padding-bottom: ${props => props.theme.margins.base_x2};
  margin: 0 ${props => props.theme.margins.base_x2};
  background: ${props => props.theme.palette.white};
  border-bottom: ${rem(1)} solid ${props => props.theme.palette.grey50};
  z-index: 10;

  span {
    padding: 0 ${props => props.theme.margins.base};

    &:last-of-type {
      font-weight: ${props => props.theme.fonts.weights.bold};
    }
  }

  ${props => props.theme.breakpoints.tablet} {
    width: 100%;
    max-width: ${rem(364)};
    left: calc(50% - ${rem(182)});
    margin: 0;
  }

  ${props => props.theme.breakpoints.desktop} {
    display: none;
  }
`;
